import { useState, useMemo, useEffect, useCallback, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  Check,
  CheckCircle,
  ChevronsUpDown,
  Clock,
  Copy,
  Download,
  FilterX,
  LayoutGrid,
  List,
  Lock,
  LockOpen,
  Printer,
  RefreshCw,
  Settings2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCalibrationCertificates,
  useCalibrationSummary,
  useCalibrationOrganisations,
  useToggleCalibrationLock,
  calibrationCertificateKeys,
} from '../hooks/useCalibrationCertificates';
import { useCalibrationFilters } from '../hooks/useCalibrationFilters';
import { printProbeTag } from '../utils/printProbeTag';
import { CalibrationCertificateDialog } from '../components/CalibrationCertificateDialog';
import { CalibrationCertificateListView } from '../components/CalibrationCertificateListView';
import { SavedViewsDropdown } from '../components/SavedViewsDropdown';
import { exportCalibrationCertificatesCSV } from '../api';
import {
  getCertificateStatus,
  formatCertificateDate,
} from '../utils/certificateStatus';
import type { CalibrationCertificate, CalibrationCertificateFilters } from '../types';

// ---------------------------------------------------------------------------
// Column definition interface
// ---------------------------------------------------------------------------

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'searchable-select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

type Translate = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escapeHtml(text: string | null | undefined): string {
  if (!text) return '-';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getStatusLabel(daysUntilDue: number | null): string {
  if (daysUntilDue === null) return 'unknown';
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= 30) return 'due_soon';
  if (daysUntilDue <= 90) return 'upcoming';
  return 'later';
}

function dateOffsetString(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

// ---------------------------------------------------------------------------
// Column key registry (language-independent \u2014 used by visibility/storage helpers)
// ---------------------------------------------------------------------------

const COLUMN_KEY_DEFAULTS: Array<{ key: string; defaultVisible?: boolean }> = [
  { key: 'certNumber' },
  { key: 'probeTag' },
  { key: 'locked' },
  { key: 'equipmentName' },
  { key: 'streamName' },
  { key: 'dueDate' },
  { key: 'daysUntilDue' },
  { key: 'status' },
  { key: 'mongoOrganisationId' },
  { key: 'sensorName', defaultVisible: false },
  { key: 'streamSerialNumber', defaultVisible: false },
  { key: 'calibrationDate', defaultVisible: false },
  { key: 'issueDate', defaultVisible: false },
  { key: 'verifiedByName', defaultVisible: false },
  { key: 'verifiedAt', defaultVisible: false },
  { key: 'equipmentSerialNumber', defaultVisible: false },
  { key: 'equipmentManufacturer', defaultVisible: false },
  { key: 'equipmentModel', defaultVisible: false },
  { key: 'probeManufacturer', defaultVisible: false },
  { key: 'probeModel', defaultVisible: false },
  { key: 'sensorType', defaultVisible: false },
  { key: 'siteName', defaultVisible: false },
  { key: 'siteAddress', defaultVisible: false },
  { key: 'locationName', defaultVisible: false },
  { key: 'equipmentType', defaultVisible: false },
  { key: 'technicianName', defaultVisible: false },
  { key: 'revision', defaultVisible: false },
];

// ---------------------------------------------------------------------------
// Column definitions factory (built per-render with active translator)
// ---------------------------------------------------------------------------

function buildColumns(t: Translate): ColumnDef<CalibrationCertificate>[] {
  return [
    {
      key: 'certNumber',
      label: t('calibration.certNumber', 'Cert #'),
      filterType: 'text',
      getValue: (c) => c.certNumber,
      render: (c) => (
        <span className="font-mono text-sm font-medium">{c.certNumber}</span>
      ),
    },
    {
      key: 'probeTag',
      label: 'PPT',
      noFilter: true,
      align: 'center',
      getValue: () => '',
      render: (c) => (
        <button
          type="button"
          title={t('calibration.printProbeTag', 'Print Probe Tag')}
          aria-label={t('calibration.printProbeTag', 'Print Probe Tag')}
          onClick={(e) => {
            e.stopPropagation();
            printProbeTag(c);
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
        >
          <Printer className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      ),
    },
    {
      key: 'locked',
      label: t('calibration.col.locked', 'Locked'),
      filterType: 'select',
      filterOptions: [
        { label: t('calibration.col.lockedTrue', 'Locked'), value: 'true' },
        { label: t('calibration.col.lockedFalse', 'Unlocked'), value: 'false' },
      ],
      getValue: (c) => (c.locked ? 'true' : 'false'),
      render: (c) =>
        c.locked ? (
          <Lock className="h-4 w-4 text-muted-foreground" />
        ) : (
          <LockOpen className="h-4 w-4 text-muted-foreground/50" />
        ),
    },
    {
      key: 'equipmentName',
      label: t('calibration.equipment', 'Equipment'),
      sortKey: 'equipment_name',
      filterType: 'text',
      getValue: (c) => c.equipmentName || '',
      render: (c) => (
        <span className="max-w-[180px] truncate block">
          {c.equipmentName || '\u2014'}
        </span>
      ),
    },
    {
      key: 'streamName',
      label: t('calibration.stream', 'Stream/Probe'),
      sortKey: 'stream_name',
      filterType: 'text',
      getValue: (c) => c.streamName || '',
      render: (c) => (
        <div className="max-w-[150px]">
          <div className="truncate">{c.streamName || '\u2014'}</div>
          {c.streamSerialNumber && (
            <div className="text-xs text-muted-foreground truncate">
              {c.streamSerialNumber}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'dueDate',
      label: t('calibration.dueDate', 'Due Date'),
      sortKey: 'due_date',
      filterType: 'date-range',
      getValue: (c) => c.dueDate || '',
      render: (c) => formatCertificateDate(c.dueDate),
    },
    {
      key: 'daysUntilDue',
      label: t('calibration.daysUntilDue', 'Days Until Due'),
      sortKey: 'days_until_due',
      filterType: 'number-range',
      getValue: (c) => c.daysUntilDue,
      render: (c) => {
        const statusInfo = getCertificateStatus(c.daysUntilDue, t);
        if (c.daysUntilDue === null) return '\u2014';
        if (c.daysUntilDue < 0)
          return (
            <span className={cn('font-medium', statusInfo.color)}>
              {Math.abs(c.daysUntilDue)} {t('calibration.daysOverdue', 'days overdue')}
            </span>
          );
        return (
          <span className={statusInfo.color}>
            {c.daysUntilDue} {t('calibration.days', 'days')}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: t('calibration.statusLabel', 'Status'),
      filterType: 'select',
      filterOptions: [
        { label: t('calibration.statusOverdue', 'Overdue'), value: 'overdue' },
        { label: t('calibration.statusDueSoon', 'Due Soon'), value: 'due_soon' },
        { label: t('calibration.statusUpcoming', 'Upcoming'), value: 'upcoming' },
        { label: t('calibration.statusLater', 'Later'), value: 'later' },
      ],
      getValue: (c) => getStatusLabel(c.daysUntilDue),
      render: (c) => {
        const statusInfo = getCertificateStatus(c.daysUntilDue, t);
        return (
          <Badge
            variant={
              statusInfo.variant as
                | 'destructive'
                | 'default'
                | 'secondary'
                | 'outline'
            }
          >
            {statusInfo.label}
          </Badge>
        );
      },
    },
    {
      key: 'mongoOrganisationId',
      label: t('calibration.organisation', 'Organisation'),
      sortKey: 'organisation_name',
      filterType: 'searchable-select',
      getValue: (c) => c.mongoOrganisationId || '',
      render: (c) => (
        <span className="text-muted-foreground max-w-[150px] truncate block">
          {c.organisationName || '\u2014'}
        </span>
      ),
    },
    // ----- Hidden by default columns -----
    {
      key: 'sensorName',
      label: t('calibration.col.sensorName', 'Sensor Name'),
      filterType: 'text',
      getValue: (c) => c.sensorName || '',
      defaultVisible: false,
    },
    {
      key: 'streamSerialNumber',
      label: t('calibration.col.probeSerial', 'Probe Serial'),
      filterType: 'text',
      getValue: (c) => c.streamSerialNumber || '',
      defaultVisible: false,
    },
    {
      key: 'calibrationDate',
      label: t('calibration.col.calibrationDate', 'Calibration Date'),
      filterType: 'date-range',
      getValue: (c) => c.calibrationDate || '',
      render: (c) => formatCertificateDate(c.calibrationDate),
      defaultVisible: false,
    },
    {
      key: 'issueDate',
      label: t('calibration.col.issueDate', 'Issue Date'),
      filterType: 'date-range',
      getValue: (c) => c.issueDate || '',
      render: (c) => formatCertificateDate(c.issueDate),
      defaultVisible: false,
    },
    {
      key: 'verifiedByName',
      label: t('calibration.col.verifiedBy', 'Verified By'),
      filterType: 'text',
      getValue: (c) => c.verifiedByName || '',
      defaultVisible: false,
    },
    {
      key: 'verifiedAt',
      label: t('calibration.col.verifiedAt', 'Verified At'),
      filterType: 'date-range',
      getValue: (c) => c.verifiedAt || '',
      render: (c) => formatCertificateDate(c.verifiedAt),
      defaultVisible: false,
    },
    {
      key: 'equipmentSerialNumber',
      label: t('calibration.col.equipmentSerial', 'Equipment S/N'),
      filterType: 'text',
      getValue: (c) => c.equipmentSerialNumber || '',
      defaultVisible: false,
    },
    {
      key: 'equipmentManufacturer',
      label: t('calibration.col.equipmentManufacturer', 'Equipment Manufacturer'),
      filterType: 'text',
      getValue: (c) => c.equipmentManufacturer || '',
      defaultVisible: false,
    },
    {
      key: 'equipmentModel',
      label: t('calibration.col.equipmentModel', 'Equipment Model'),
      filterType: 'text',
      getValue: (c) => c.equipmentModel || '',
      defaultVisible: false,
    },
    {
      key: 'probeManufacturer',
      label: t('calibration.col.probeManufacturer', 'Probe Manufacturer'),
      filterType: 'text',
      getValue: (c) => c.probeManufacturer || '',
      defaultVisible: false,
    },
    {
      key: 'probeModel',
      label: t('calibration.col.probeModel', 'Probe Model'),
      filterType: 'text',
      getValue: (c) => c.probeModel || '',
      defaultVisible: false,
    },
    {
      key: 'sensorType',
      label: t('calibration.col.sensorType', 'Sensor Type'),
      filterType: 'text',
      getValue: (c) => c.sensorType || '',
      defaultVisible: false,
    },
    {
      key: 'siteName',
      label: t('calibration.col.site', 'Site'),
      filterType: 'text',
      getValue: (c) => c.siteName || '',
      render: (c) => (
        <span className="max-w-[150px] truncate block">
          {c.siteName || '\u2014'}
        </span>
      ),
      defaultVisible: false,
    },
    {
      key: 'siteAddress',
      label: t('calibration.col.siteAddress', 'Site Address'),
      filterType: 'text',
      getValue: (c) => c.siteAddress || '',
      defaultVisible: false,
    },
    {
      key: 'locationName',
      label: t('calibration.col.location', 'Location'),
      filterType: 'text',
      getValue: (c) => c.locationName || '',
      render: (c) => (
        <span className="max-w-[150px] truncate block">
          {c.locationName || '\u2014'}
        </span>
      ),
      defaultVisible: false,
    },
    {
      key: 'equipmentType',
      label: t('calibration.col.equipmentType', 'Equipment Type'),
      filterType: 'text',
      getValue: (c) => c.equipmentType || '',
      render: (c) => c.equipmentType || <span className="text-muted-foreground">{'\u2014'}</span>,
      defaultVisible: false,
    },
    {
      key: 'technicianName',
      label: t('calibration.col.technician', 'Technician'),
      filterType: 'text',
      getValue: (c) => c.technicianName || '',
      render: (c) => c.technicianName || <span className="text-muted-foreground">{'\u2014'}</span>,
      defaultVisible: false,
    },
    {
      key: 'revision',
      label: t('calibration.col.revision', 'Revision'),
      filterType: 'number-range',
      getValue: (c) => c.revision,
      defaultVisible: false,
    },
  ];
}

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const FILTER_STORAGE_KEY = 'atek-calibration-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-calibration-column-visibility';
const PDF_COLUMNS_STORAGE_KEY = 'atek-calibration-pdf-columns';
const SAVED_VIEWS_FEATURE = 'calibration-certificates';
const EXPORTABLE_COLUMN_KEYS = new Set(COLUMN_KEY_DEFAULTS.map((c) => c.key));
const DEFAULT_VISIBLE_PDF_KEYS = COLUMN_KEY_DEFAULTS
  .filter((c) => c.defaultVisible !== false)
  .map((c) => c.key);

// ---------------------------------------------------------------------------
// Column visibility helpers
// ---------------------------------------------------------------------------

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMN_KEY_DEFAULTS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function getDefaultPdfColumnKeys(): string[] {
  return [...DEFAULT_VISIBLE_PDF_KEYS];
}

function getColumnsByKeys(
  columns: ColumnDef<CalibrationCertificate>[],
  keys: string[],
): ColumnDef<CalibrationCertificate>[] {
  const selected = new Set(keys);
  return columns.filter((col) => !col.noFilter && selected.has(col.key));
}

function loadPdfColumnKeys(): string[] | null {
  try {
    const stored = localStorage.getItem(PDF_COLUMNS_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return null;

    const keys = parsed.filter(
      (key): key is string => typeof key === 'string' && EXPORTABLE_COLUMN_KEYS.has(key),
    );
    return keys.length > 0 ? keys : null;
  } catch {
    return null;
  }
}

function savePdfColumnKeys(keys: string[]) {
  localStorage.setItem(PDF_COLUMNS_STORAGE_KEY, JSON.stringify(keys));
}

function parseSavedColumnFilters(value: unknown): Record<string, string> {
  if (!value) return {};

  const parsed = (() => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return value;
  })();

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>)
      .filter(([, filterValue]) => filterValue !== undefined && filterValue !== null && filterValue !== '')
      .map(([key, filterValue]) => [key, String(filterValue)]),
  );
}

function getPdfColumnValue(
  cert: CalibrationCertificate,
  column: ColumnDef<CalibrationCertificate>,
  t: Translate,
): string {
  if (column.key === 'locked') {
    return cert.locked
      ? t('calibration.locked', 'Locked')
      : t('calibration.unlocked', 'Unlocked');
  }

  if (column.key === 'daysUntilDue') {
    if (cert.daysUntilDue === null) return '';
    if (cert.daysUntilDue < 0) {
      return `${Math.abs(cert.daysUntilDue)} ${t('calibration.daysOverdue', 'days overdue')}`;
    }
    return `${cert.daysUntilDue} ${t('calibration.daysRemaining', 'days remaining')}`;
  }

  if (column.key === 'status') {
    return getCertificateStatus(cert.daysUntilDue, t).label;
  }

  if (
    column.key === 'dueDate' ||
    column.key === 'issueDate' ||
    column.key === 'calibrationDate' ||
    column.key === 'verifiedAt'
  ) {
    return formatCertificateDate(String(column.getValue(cert) || ''));
  }

  return String(column.getValue(cert) ?? '');
}

function getPdfStatusClass(cert: CalibrationCertificate): string {
  if (cert.daysUntilDue === null) return '';
  if (cert.daysUntilDue < 0) return 'overdue';
  if (cert.daysUntilDue <= 30) return 'due-soon';
  return '';
}

function copyToClipboard(
  items: CalibrationCertificate[],
  columns: ColumnDef<CalibrationCertificate>[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(
  items: CalibrationCertificate[],
  columns: ColumnDef<CalibrationCertificate>[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// SortableHeader component
// ---------------------------------------------------------------------------

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// SearchableSelectFilter component (combobox with search)
// ---------------------------------------------------------------------------

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const { t } = useTranslation('intelligence');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try {
      return value ? JSON.parse(value) : [];
    } catch {
      return [];
    }
  }, [value]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || t('calibration.filter.oneSelected', '1 selected');
    }
    return t('calibration.filter.manySelected', '{{count}} selected', {
      count: selected.length,
    });
  }, [selected, options, t]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full text-xs justify-between font-normal"
          >
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => {
              onChange(colKey, '');
              setOpen(false);
            }}
            className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2"
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('calibration.filter.searchPlaceholder', 'Search...')}
            value={search}
            onValueChange={setSearch}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              {t('calibration.filter.noResults', 'No results')}
            </CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className="text-xs"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selected.includes(opt.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {selected.includes(opt.value) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Filter row rendering
// ---------------------------------------------------------------------------

function renderColumnFilter(
  col: ColumnDef<CalibrationCertificate>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  t: Translate,
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Searchable select filter (combobox with search)
  if (col.filterType === 'searchable-select') {
    const options = searchableSelectOptions?.[col.key] || [];
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <SearchableSelectFilter
          colKey={col.key}
          options={options}
          value={columnFilters[col.key] || ''}
          onChange={setColumnFilter}
        />
      </TableHead>
    );
  }

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key]
          ? JSON.parse(columnFilters[col.key])
          : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length === 0
                ? '\u2014'
                : selected.length === 1
                  ? t('calibration.filter.oneSelected', '1 selected')
                  : t('calibration.filter.manySelected', '{{count}} selected', {
                      count: selected.length,
                    })}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${to}`)
            }
            title={t('calibration.filter.fromTitle', 'From')}
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) =>
              setColumnFilter(col.key, `${from}>>${e.target.value}`)
            }
            title={t('calibration.filter.toTitle', 'To')}
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) =>
              setColumnFilter(col.key, `${e.target.value}>>${max}`)
            }
            placeholder={t('calibration.filter.minPlaceholder', 'Min')}
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) =>
              setColumnFilter(col.key, `${min}>>${e.target.value}`)
            }
            placeholder={t('calibration.filter.maxPlaceholder', 'Max')}
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ViewMode = 'table' | 'list';
type SummaryFilter = 'total' | 'overdue' | 'due30' | 'due60' | 'due90' | 'later';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CalibrationCertificatesPage({ showAll = false }: { showAll?: boolean } = {}) {
  const { t } = useTranslation('intelligence');
  const queryClient = useQueryClient();

  // ---- URL-synced server-side filters -------------------------------------
  const {
    filters,
    rawFilters,
    setFilter,
    setFilters,
    resetFilters,
    loadFromSavedView,
    getFiltersForSave,
    hasActiveFilters,
  } = useCalibrationFilters();

  // ---- View mode ----------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // ---- Selection for bulk actions -----------------------------------------
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSelectedIdRef = useRef<string | null>(null);
  const toggleLockMutation = useToggleCalibrationLock();

  // ---- Certificate detail dialog ------------------------------------------
  const [selectedMongoId, setSelectedMongoId] = useState<string | null>(null);

  // ---- Export loading state -----------------------------------------------
  const [isExporting, setIsExporting] = useState(false);
  const [savedPdfColumnKeys, setSavedPdfColumnKeys] = useState<string[] | null>(
    loadPdfColumnKeys,
  );
  const [pdfColumnDraft, setPdfColumnDraft] = useState<string[]>([]);
  const [isPdfColumnDialogOpen, setIsPdfColumnDialogOpen] = useState(false);

  // ---- Column filters (client-side, persisted) ----------------------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => {
      try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (stored) return JSON.parse(stored);
      } catch {
        /* ignore */
      }
      return {};
    },
  );

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ---- Column visibility --------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  // ---- Translated column definitions --------------------------------------
  const columns = useMemo(() => buildColumns(t), [t]);
  const exportableColumns = useMemo(
    () => columns.filter((col) => !col.noFilter),
    [columns],
  );

  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.key] !== false),
    [columns, columnVisibility],
  );
  const visiblePdfColumnKeys = useMemo(
    () => visibleColumns.filter((col) => !col.noFilter).map((col) => col.key),
    [visibleColumns],
  );

  // ---- Organisation searchable-select options -----------------------------
  const organisationsQuery = useCalibrationOrganisations({
    showAll,
    accountId: rawFilters.accountId || undefined,
    dueInDays: filters.dueInDays,
    dueDateFrom: filters.dueDateFrom,
    dueDateTo: filters.dueDateTo,
    includeOverdue: filters.includeOverdue,
    status: filters.status,
  });
  const orgOptions = useMemo(() => {
    const orgs = organisationsQuery.data || [];
    return orgs
      .filter((o) => o.organisationName && o.mongoOrganisationId)
      .map((o) => ({ label: o.organisationName!, value: o.mongoOrganisationId! }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [organisationsQuery.data]);

  const searchableSelectOptions = useMemo(
    () => ({ mongoOrganisationId: orgOptions }),
    [orgOptions],
  );

  // ---- Mode B: send all column filters to the server ----------------------
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState(columnFilters);
  const isFirstColumnFilterRender = useRef(true);
  const setFilterRef = useRef(setFilter);
  const columnFiltersSignature = useMemo(
    () => JSON.stringify(columnFilters),
    [columnFilters],
  );

  useEffect(() => {
    setFilterRef.current = setFilter;
  }, [setFilter]);

  useEffect(() => {
    if (isFirstColumnFilterRender.current) {
      isFirstColumnFilterRender.current = false;
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
      setFilterRef.current('pageNumber', 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnFilters, columnFiltersSignature]);

  const activeServerColumnFilters = useMemo(() => {
    const active = Object.fromEntries(
      Object.entries(debouncedColumnFilters).filter(([, value]) => value),
    );
    return Object.keys(active).length > 0 ? active : undefined;
  }, [debouncedColumnFilters]);

  const effectiveFilters = useMemo(() => {
    return {
      ...filters,
      search: filters.search || undefined,
      columnFilters: activeServerColumnFilters,
      showAll: showAll || undefined,
    };
  }, [filters, activeServerColumnFilters, showAll]);

  // ---- Data fetching ------------------------------------------------------
  const certificatesQuery = useCalibrationCertificates(effectiveFilters);
  const summaryQuery = useCalibrationSummary({
    organisationId:
      rawFilters.organisationId === 'all'
        ? undefined
        : rawFilters.organisationId,
    accountId: rawFilters.accountId || undefined,
    showAll,
  });
  const certificates = useMemo(
    () => certificatesQuery.data?.items || [],
    [certificatesQuery.data?.items],
  );
  const total = certificatesQuery.data?.total || 0;
  const totalPages = Math.ceil(total / (rawFilters.pageLength || 50));
  const summary = summaryQuery.data;

  // ---- Client-side column filtering (within current page) -----------------
  const filteredCertificates = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return certificates;

    return certificates.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select' || col.filterType === 'searchable-select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return (
              selected.length === 0 ||
              selected.includes(String(rawValue || ''))
            );
          } catch {
            return true;
          }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [certificates, columnFilters, columns]);
  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);
  const currentSavedViewFilters = useMemo(() => {
    const savedFilters = { ...getFiltersForSave() };
    const activeColumnFilters = Object.fromEntries(
      Object.entries(columnFilters).filter(([, value]) => value),
    );

    if (Object.keys(activeColumnFilters).length > 0) {
      savedFilters.columnFilters = activeColumnFilters;
    }

    return savedFilters;
  }, [columnFilters, getFiltersForSave]);
  const hasSaveableFilters = hasActiveFilters || hasActiveColumnFilters;

  // ---- Selected certificates (from filtered) ------------------------------
  const selectedCertificates = filteredCertificates.filter((c) =>
    selectedIds.has(c.id),
  );
  const selectedPdfColumnKeys = savedPdfColumnKeys ?? visiblePdfColumnKeys;

  const openPdfColumnDialog = () => {
    setPdfColumnDraft(selectedPdfColumnKeys);
    setIsPdfColumnDialogOpen(true);
  };

  const setPdfDraftColumn = (key: string, checked: boolean) => {
    setPdfColumnDraft((prev) => {
      const next = checked
        ? Array.from(new Set([...prev, key]))
        : prev.filter((item) => item !== key);
      return getColumnsByKeys(columns, next).map((col) => col.key);
    });
  };

  const handleLoadSavedView = useCallback(
    (viewFilters: Record<string, unknown>) => {
      const { columnFilters: savedColumnFilters, ...urlFilters } = viewFilters;
      const nextColumnFilters = parseSavedColumnFilters(savedColumnFilters);

      setColumnFilters(nextColumnFilters);
      setDebouncedColumnFilters(nextColumnFilters);
      loadFromSavedView(urlFilters);
    },
    [loadFromSavedView],
  );

  // ---- Handlers -----------------------------------------------------------
  const handleSort = useCallback(
    (column: string) => {
      if (rawFilters.sortKey === column) {
        setFilters({
          sortDirection: rawFilters.sortDirection === 1 ? -1 : 1,
        });
      } else {
        setFilters({
          sortKey: column,
          sortDirection: 1,
        });
      }
    },
    [rawFilters.sortKey, rawFilters.sortDirection, setFilters],
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredCertificates.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
    lastSelectedIdRef.current = id;
  };

  const handleCertNumberClick = (
    cert: CalibrationCertificate,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const id = cert.id;

    if (e.shiftKey && lastSelectedIdRef.current) {
      const ids = filteredCertificates.map((c) => c.id);
      const start = ids.indexOf(lastSelectedIdRef.current);
      const end = ids.indexOf(id);
      if (start !== -1 && end !== -1) {
        const [from, to] = start <= end ? [start, end] : [end, start];
        const next = new Set(selectedIds);
        for (let i = from; i <= to; i += 1) next.add(ids[i]);
        setSelectedIds(next);
        return;
      }
    }

    // Plain click and Ctrl/Cmd+click both toggle the single row.
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    lastSelectedIdRef.current = id;
  };

  const handleExportAllCSV = async () => {
    try {
      setIsExporting(true);
      const blob = await exportCalibrationCertificatesCSV(effectiveFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calibration-certificates-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(
        t('calibration.toast.exportedRows', 'Exported {{count}} rows', {
          count: total,
        }),
      );
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('calibration.toast.exportFailed', 'Failed to export all data'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPageCSV = () => {
    exportToCSV(
      filteredCertificates,
      visibleColumns,
      'calibration-certificates-page',
    );
    toast.success(
      t('calibration.toast.exportedRows', 'Exported {{count}} rows', {
        count: filteredCertificates.length,
      }),
    );
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(filteredCertificates, visibleColumns);
      toast.success(t('calibration.toast.copied', 'Copied to clipboard'));
    } catch {
      toast.error(t('calibration.toast.copyFailed', 'Failed to copy to clipboard'));
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: calibrationCertificateKeys.all });
  };

  const handleRowClick = (cert: CalibrationCertificate) => {
    setSelectedMongoId(cert.mongoCalibrationId);
  };

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedIdRef.current = null;
  }, []);

  // Keyboard shortcuts: Esc clears selection, Ctrl/Cmd+A selects all visible.
  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditable =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      if (e.key === 'Escape' && selectedIds.size > 0 && !isEditable) {
        clearSelection();
        return;
      }

      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'a' || e.key === 'A') &&
        !isEditable &&
        filteredCertificates.length > 0
      ) {
        e.preventDefault();
        setSelectedIds(new Set(filteredCertificates.map((c) => c.id)));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIds.size, filteredCertificates, clearSelection]);

  const applySummaryFilter = useCallback(
    (filter: SummaryFilter) => {
      const nextFilters: Partial<CalibrationCertificateFilters> = {
        status: undefined,
        dueDateFrom: undefined,
        dueDateTo: undefined,
      };

      if (filter === 'overdue') {
        nextFilters.status = ['overdue'];
      } else if (filter === 'due30') {
        nextFilters.dueDateFrom = dateOffsetString(0);
        nextFilters.dueDateTo = dateOffsetString(29);
      } else if (filter === 'due60') {
        nextFilters.dueDateFrom = dateOffsetString(30);
        nextFilters.dueDateTo = dateOffsetString(59);
      } else if (filter === 'due90') {
        nextFilters.dueDateFrom = dateOffsetString(60);
        nextFilters.dueDateTo = dateOffsetString(89);
      } else if (filter === 'later') {
        nextFilters.status = ['later'];
      }

      setFilters(nextFilters);
    },
    [setFilters],
  );

  const isSummaryFilterActive = useCallback(
    (filter: SummaryFilter) => {
      const status = rawFilters.status;

      if (filter === 'total') {
        return status.length === 0 && !rawFilters.dueDateFrom && !rawFilters.dueDateTo;
      }
      if (filter === 'overdue') {
        return status.length === 1 && status[0] === 'overdue';
      }
      if (filter === 'later') {
        return status.length === 1 && status[0] === 'later';
      }

      const ranges: Record<Exclude<SummaryFilter, 'total' | 'overdue' | 'later'>, [string, string]> = {
        due30: [dateOffsetString(0), dateOffsetString(29)],
        due60: [dateOffsetString(30), dateOffsetString(59)],
        due90: [dateOffsetString(60), dateOffsetString(89)],
      };
      const [from, to] = ranges[filter];
      return rawFilters.dueDateFrom === from && rawFilters.dueDateTo === to;
    },
    [rawFilters.dueDateFrom, rawFilters.dueDateTo, rawFilters.status],
  );

  const getSummaryCardProps = (filter: SummaryFilter) => ({
    role: 'button' as const,
    tabIndex: 0,
    'aria-pressed': isSummaryFilterActive(filter),
    onClick: () => applySummaryFilter(filter),
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        applySummaryFilter(filter);
      }
    },
    className: cn(
      'cursor-pointer transition hover:border-primary/60 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      isSummaryFilterActive(filter) && 'border-primary bg-primary/5 shadow-sm',
    ),
  });

  // ---- Print handler ------------------------------------------------------
  const handlePrint = useCallback(() => {
    if (selectedCertificates.length === 0) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('calibration.printReport.title', 'Calibration Certificates Due Report')}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            margin: 20px;
          }
          h1 {
            font-size: 18px;
            margin-bottom: 5px;
          }
          .subtitle {
            color: #666;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #fafafa;
          }
          .overdue {
            color: #dc2626;
            font-weight: bold;
          }
          .due-soon {
            color: #ea580c;
          }
          .print-date {
            text-align: right;
            color: #666;
            font-size: 10px;
            margin-bottom: 10px;
          }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="print-date">${t('calibration.printReport.printedAt', 'Printed: {{date}}', { date: new Date().toLocaleString() })}</div>
        <h1>${t('calibration.printReport.title', 'Calibration Certificates Due Report')}</h1>
        <div class="subtitle">${t('calibration.printReport.selectedCount', '{{count}} certificate(s) selected', { count: selectedCertificates.length })}</div>
        <table>
          <thead>
            <tr>
              <th>${t('calibration.certNumber', 'Cert #')}</th>
              <th>${t('calibration.col.issueDate', 'Issue Date')}</th>
              <th>${t('calibration.dueDate', 'Due Date')}</th>
              <th>${t('calibration.col.client', 'Client')}</th>
              <th>${t('calibration.col.sensor', 'Sensor')}</th>
              <th>${t('calibration.col.location', 'Location')}</th>
              <th>${t('calibration.col.equipmentType', 'Equipment Type')}</th>
            </tr>
          </thead>
          <tbody>
            ${selectedCertificates
              .map((cert) => {
                const isOverdue =
                  cert.daysUntilDue !== null && cert.daysUntilDue < 0;
                const isDueSoon =
                  cert.daysUntilDue !== null &&
                  cert.daysUntilDue >= 0 &&
                  cert.daysUntilDue <= 30;
                const statusClass = isOverdue
                  ? 'overdue'
                  : isDueSoon
                    ? 'due-soon'
                    : '';

                const clientParts = [
                  cert.organisationName,
                  cert.siteName,
                  cert.siteAddress,
                ].filter(Boolean);
                const clientInfo = escapeHtml(
                  clientParts.join(' - ') || null,
                );

                return `
                  <tr>
                    <td>${escapeHtml(cert.certNumber)}</td>
                    <td>${formatCertificateDate(cert.issueDate)}</td>
                    <td class="${statusClass}">${formatCertificateDate(cert.dueDate)}${isOverdue ? t('calibration.printReport.overdueSuffix', ' (Overdue)') : ''}</td>
                    <td>${clientInfo}</td>
                    <td>${escapeHtml(cert.streamName || cert.equipmentName)}</td>
                    <td>${escapeHtml(cert.locationName)}</td>
                    <td>${escapeHtml(cert.equipmentType)}</td>
                  </tr>
                `;
              })
              .join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  }, [selectedCertificates, t]);

  // ---- PDF export handler -------------------------------------------------
  const handleExportPDF = useCallback((pdfColumns: ColumnDef<CalibrationCertificate>[]) => {
    if (selectedCertificates.length === 0 || pdfColumns.length === 0) return;

    type GroupedData = Map<string, Map<string, CalibrationCertificate[]>>;
    const grouped: GroupedData = new Map();

    selectedCertificates.forEach((cert) => {
      const siteName =
        cert.siteName || t('calibration.unknownSite', 'Unknown Site');
      const locationName =
        cert.locationName ||
        t('calibration.unknownLocation', 'Unknown Location');

      if (!grouped.has(siteName)) {
        grouped.set(siteName, new Map());
      }
      const siteMap = grouped.get(siteName)!;

      if (!siteMap.has(locationName)) {
        siteMap.set(locationName, []);
      }
      siteMap.get(locationName)!.push(cert);
    });

    const sortedSites = Array.from(grouped.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );

    let siteContent = '';
    sortedSites.forEach(([siteName, locations]) => {
      const sortedLocations = Array.from(locations.entries()).sort((a, b) =>
        a[0].localeCompare(b[0]),
      );

      let locationContent = '';
      sortedLocations.forEach(([locationName, certs]) => {
        const sortedCerts = [...certs].sort((a, b) => {
          if (a.dueDate === null) return 1;
          if (b.dueDate === null) return -1;
          return (
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
          );
        });

        let certRows = '';
        sortedCerts.forEach((cert) => {
          certRows += `
            <tr>
              ${pdfColumns
                .map((column) => {
                  const statusClass =
                    column.key === 'dueDate' ||
                    column.key === 'daysUntilDue' ||
                    column.key === 'status'
                      ? getPdfStatusClass(cert)
                      : '';
                  const cellClasses = [
                    `pdf-${column.key}`,
                    column.key === 'certNumber' ? 'cert-number' : '',
                    statusClass,
                  ].filter(Boolean);
                  return `<td class="${cellClasses.join(' ')}">${escapeHtml(getPdfColumnValue(cert, column, t))}</td>`;
                })
                .join('')}
            </tr>
          `;
        });

        locationContent += `
          <div class="location-section">
            <div class="location-header">
              <span class="location-name">${escapeHtml(locationName)}</span>
              <span class="location-count">(${certs.length})</span>
            </div>
            <table class="certificates-table">
              <thead>
                <tr>
                  ${pdfColumns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${certRows}
              </tbody>
            </table>
          </div>
        `;
      });

      siteContent += `
        <div class="site-section">
          <div class="site-header">
            <span class="site-name">${escapeHtml(siteName)}</span>
          </div>
          ${locationContent}
        </div>
      `;
    });

    const uniqueOrgNames = [
      ...new Set(
        selectedCertificates
          .map((cert) => cert.organisationName)
          .filter((name): name is string => !!name),
      ),
    ].sort();

    const orgName =
      uniqueOrgNames.length > 0
        ? uniqueOrgNames.join(', ')
        : t('calibration.unknownOrg', 'Unknown Organisation');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('calibration.pdfTitle', 'Calibration Certificates Report')}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 11px; line-height: 1.4; margin: 0; padding: 20px; color: #1a1a1a; }
          .header { border-bottom: 2px solid #2563eb; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 20px; margin: 0 0 5px 0; color: #1e40af; }
          .header .org-name { font-size: 14px; color: #4b5563; margin-bottom: 5px; }
          .header .meta { font-size: 10px; color: #6b7280; }
          .site-section { margin-bottom: 20px; page-break-inside: avoid; }
          .site-header { color: #1e40af; padding: 8px 0; border-bottom: 1px solid #93c5fd; font-size: 14px; font-weight: 700; }
          .location-section { margin: 10px 0 16px 0; page-break-inside: avoid; }
          .location-header { padding: 4px 0 6px 0; font-size: 12px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 6px; }
          .location-count { font-weight: normal; color: #6b7280; font-size: 11px; }
          .certificates-table { width: 100%; border-collapse: collapse; margin: 0; table-layout: fixed; }
          .certificates-table th,
          .certificates-table td { border: 1px solid #d1d5db; padding: 5px 6px; text-align: left; vertical-align: top; }
          .certificates-table th { background: #f3f4f6; color: #374151; font-size: 9px; font-weight: 700; text-transform: uppercase; }
          .certificates-table td { font-size: 10px; color: #111827; }
          .certificates-table tr:nth-child(even) td { background: #fafafa; }
          .pdf-equipmentName, .pdf-streamName { font-weight: 600; }
          .cert-number { font-family: 'Monaco', 'Consolas', monospace; font-size: 9px; color: #374151; }
          .overdue { color: #dc2626 !important; }
          td.overdue { color: #dc2626 !important; font-weight: 700; }
          .due-soon { color: #ea580c !important; }
          td.due-soon { color: #ea580c !important; font-weight: 600; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #9ca3af; text-align: center; }
          @media print { body { padding: 10px; } .site-section, .location-section, tr { page-break-inside: avoid; } @page { margin: 15mm; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${t('calibration.pdfTitle', 'Calibration Certificates Report')}</h1>
          <div class="org-name">${escapeHtml(orgName)}</div>
          <div class="meta">
            ${t('calibration.generated', 'Generated')}: ${new Date().toLocaleString()} |
            ${t('calibration.totalCertificates', 'Total')}: ${selectedCertificates.length} ${t('calibration.certificates', 'certificates')}
          </div>
        </div>
        ${siteContent}
        <div class="footer">
          ${t('calibration.pdfFooter', 'ATEK Calibration Management System')} - ${new Date().toLocaleDateString()}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error(
        t(
          'calibration.pdfPopupBlocked',
          'Unable to open PDF. Please allow popups for this site.',
        ),
      );
      return;
    }

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  }, [selectedCertificates, t]);

  const handleExportVisiblePDF = useCallback(() => {
    handleExportPDF(getColumnsByKeys(columns, visiblePdfColumnKeys));
  }, [handleExportPDF, visiblePdfColumnKeys, columns]);

  const handleExportCustomPDF = useCallback(() => {
    const nextKeys = getColumnsByKeys(columns, pdfColumnDraft).map((col) => col.key);
    if (nextKeys.length === 0) return;

    setSavedPdfColumnKeys(nextKeys);
    savePdfColumnKeys(nextKeys);
    setIsPdfColumnDialogOpen(false);
    handleExportPDF(getColumnsByKeys(columns, nextKeys));
  }, [handleExportPDF, pdfColumnDraft, columns]);

  // ---- Render -------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="breadcrumb flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => queryClient.invalidateQueries({ queryKey: calibrationCertificateKeys.all })}
          className="text-atek-blue hover:text-atek-blue-dark"
        >
          {t('calibration.intelligence', 'Intelligence')}
        </button>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground">{t('calibration.pageTitle', 'Due Calibrations')}</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">
            {t('calibration.pageTitle', 'Due Calibrations')}
          </h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            {t('calibration.pageSubtitle', 'Manage and track equipment calibration schedules across all organizations.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SavedViewsDropdown
            feature={SAVED_VIEWS_FEATURE}
            currentFilters={currentSavedViewFilters}
            onLoadView={handleLoadSavedView}
            hasActiveFilters={hasSaveableFilters}
          />

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportAllCSV}
            disabled={isExporting}
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? t('calibration.exporting', 'Exporting...') : t('calibration.export', 'Export CSV')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryQuery.isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { key: 'total', label: t('calibration.summaryTotal', 'Total Active'), value: summary.total, color: 'text-gray-900', icon: LayoutGrid },
            { key: 'overdue', label: t('calibration.summaryOverdue', 'Overdue'), value: summary.overdue, color: 'text-destructive', icon: AlertTriangle },
            { key: 'due30', label: t('calibration.summaryDue30', 'Due within 30 days'), value: summary.dueIn30Days, color: 'text-orange-600', icon: Clock },
            { key: 'due60', label: t('calibration.summaryDue60', 'Due within 60 days'), value: summary.dueIn60Days, color: 'text-yellow-600', icon: Clock },
            { key: 'due90', label: t('calibration.summaryDue90', 'Due within 90 days'), value: summary.dueIn90Days, color: 'text-green-600', icon: CheckCircle },
            { key: 'later', label: t('calibration.summaryLater', 'Later'), value: summary.dueAfter90Days, color: 'text-blue-600', icon: Calendar },
          ].map((item) => (
            <Card
              key={item.key}
              {...getSummaryCardProps(item.key as SummaryFilter)}
              className={cn(
                'py-2 transition-colors cursor-pointer hover:bg-muted/50',
                isSummaryFilterActive(item.key as SummaryFilter) && 'ring-2 ring-primary ring-inset bg-muted/50'
              )}
            >
              <CardHeader className="pb-1 px-3 pt-0 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-xs font-medium text-muted-foreground truncate">
                  {item.label}
                </CardTitle>
                <item.icon className="h-3.5 w-3.5 text-muted-foreground" />
              </CardHeader>
              <CardContent className="px-3 pb-0">
                <div className={cn('text-lg font-bold', item.color)}>
                  {item.value.toLocaleString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}


      {/* Results */}
      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold leading-5">
                  {t('calibration.certificates', 'Certificates')}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {certificatesQuery.data?.total || 0} {t('calibration.totalCertificates', 'certificates found')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="flex items-center gap-1 bg-muted/50 p-0.5 rounded-md border mr-2">
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setViewMode('table')}
                >
                  <List className="h-3 w-3 mr-1" />
                  {t('calibration.tableView', 'Table')}
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setViewMode('list')}
                >
                  <LayoutGrid className="h-3 w-3 mr-1" />
                  {t('calibration.cardView', 'Card view')}
                </Button>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="h-8 text-xs"
                >
                  <FilterX className="mr-2 h-3.5 w-3.5" />
                  {t('calibration.resetFilters', 'Reset')}
                </Button>
              )}

              {Object.keys(columnFilters).length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setColumnFilters({})}
                  className="h-8 text-xs"
                >
                  <X className="mr-2 h-3.5 w-3.5" />
                  {t('calibration.clearFilters', 'Clear Column Filters')}
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={certificatesQuery.isFetching}
                className="h-8 text-xs"
              >
                <RefreshCw className={cn('mr-2 h-3.5 w-3.5', certificatesQuery.isFetching && 'animate-spin')} />
                {t('calibration.refresh', 'Refresh')}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="h-8 text-xs"
              >
                <Copy className="mr-2 h-3.5 w-3.5" />
                {t('calibration.copy', 'Copy')}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isExporting}
                    className="h-8 text-xs"
                  >
                    <Download className="mr-2 h-3.5 w-3.5" />
                    {isExporting ? t('calibration.exporting', 'Exporting...') : t('calibration.csv', 'CSV')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-xs font-semibold">
                    {t('calibration.export', 'Export')}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportPageCSV} className="text-xs">
                    {t('calibration.exportPage', 'Export Page')} ({filteredCertificates.length} rows)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllCSV} className="text-xs">
                    {t('calibration.exportAll', 'Export All')} ({total})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs">
                    <Settings2 className="mr-2 h-3.5 w-3.5" />
                    {t('calibration.columns', 'Columns')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs font-semibold">
                    {t('calibration.toggleColumns', 'Toggle Columns')}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <ScrollArea className="h-72">
                    {exportableColumns.map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.key}
                        checked={isVisible(col.key)}
                        onCheckedChange={() => toggleColumn(col.key)}
                        className="text-xs"
                      >
                        {col.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs">
                    <Printer className="mr-2 h-3.5 w-3.5" />
                    {t('calibration.printDropdown', 'Print / PDF')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-xs font-semibold">{t('calibration.pdfExport', 'PDF Export')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handlePrint} disabled={selectedCertificates.length === 0} className="text-xs">
                    <Printer className="mr-2 h-3.5 w-3.5" />
                    {t('calibration.printSelected', 'Print Selected')} ({selectedCertificates.length})
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportVisiblePDF} disabled={selectedCertificates.length === 0} className="text-xs">
                    <Download className="mr-2 h-3.5 w-3.5" />
                    {t('calibration.pdfVisible', 'PDF - Visible Columns')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openPdfColumnDialog} disabled={selectedCertificates.length === 0} className="text-xs">
                    <Settings2 className="mr-2 h-3.5 w-3.5" />
                    {t('calibration.pdfCustomize', 'PDF - Customize...')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="mt-0">
            {certificatesQuery.error ? (
              <div className="text-center py-8 text-destructive px-6">
                {t('calibration.error', 'Error loading certificates')}:{' '}
                {(certificatesQuery.error as Error).message}
              </div>
            ) : viewMode === 'list' ? (
              // List (Card) View
              <div className="px-6 pb-6 pt-4">
                <CalibrationCertificateListView
                  certificates={filteredCertificates}
                  isLoading={certificatesQuery.isLoading}
                  onCertificateClick={handleRowClick}
                  selectedIds={selectedIds}
                  onSelect={handleSelectOne}
                  onToggleLock={(cert) => {
                    if (!cert.mongoCalibrationId) return;
                    toggleLockMutation.mutate({
                      mongoIds: [cert.mongoCalibrationId],
                      lock: !cert.locked,
                    });
                  }}
                />
              </div>
            ) : (
              // Table View - header (filters + labels) always visible
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    {/* ROW 1: Column filter inputs (always visible) */}
                    <TableRow className="bg-muted/30 hover:bg-muted/30 border-none">
                      {/* Checkbox column - empty filter cell */}
                      <TableHead className="w-[40px] p-1" />
                      {visibleColumns.map((col) =>
                        renderColumnFilter(
                          col,
                          columnFilters,
                          setColumnFilter,
                          t,
                          searchableSelectOptions,
                        ),
                      )}
                    </TableRow>

                    {/* ROW 2: Column labels + sort controls */}
                    <TableRow>
                      {/* Select-all checkbox */}
                      <TableHead className="w-[40px] p-1 text-center">
                        <Checkbox
                          checked={
                            selectedIds.size ===
                              filteredCertificates.length &&
                            filteredCertificates.length > 0
                          }
                          onCheckedChange={(checked) => handleSelectAll(!!checked)}
                          aria-label={t(
                            'calibration.selectAll',
                            'Select all certificates',
                          )}
                          className="mx-auto"
                        />
                      </TableHead>
                      {visibleColumns.map((col) => (
                        <TableHead
                          key={col.key}
                          className={cn(
                            'text-xs font-semibold py-2 transition-colors hover:text-atek-blue',
                            col.align === 'right' && 'text-right',
                            col.align === 'center' && 'text-center',
                          )}
                        >
                          {col.sortKey ? (
                            <SortableHeader
                              label={col.label}
                              sortKey={col.sortKey}
                              currentSortKey={rawFilters.sortKey}
                              sortDirection={rawFilters.sortDirection}
                              onSort={handleSort}
                            />
                          ) : (
                            col.label
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {/* Loading skeleton rows */}
                    {certificatesQuery.isLoading &&
                      Array.from({ length: 10 }).map((_, i) => (
                        <TableRow key={`skeleton-${i}`}>
                          <TableCell className="p-2">
                            <Skeleton className="h-4 w-4 mx-auto" />
                          </TableCell>
                          {visibleColumns.map((col) => (
                            <TableCell key={col.key} className="p-2">
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}

                    {/* Empty state */}
                    {!certificatesQuery.isLoading &&
                      filteredCertificates.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={visibleColumns.length + 1}
                            className="py-12 text-center text-muted-foreground"
                          >
                            {t(
                              'calibration.noResults',
                              'No certificates found matching your criteria.',
                            )}
                          </TableCell>
                        </TableRow>
                      )}

                    {/* Data rows */}
                    {!certificatesQuery.isLoading &&
                      filteredCertificates.map((cert) => (
                        <TableRow
                          key={cert.id}
                          className="group border-l-2 border-l-transparent transition-colors hover:border-l-atek-blue hover:bg-atek-blue/5 cursor-pointer"
                          onClick={() => handleRowClick(cert)}
                        >
                          {/* Checkbox */}
                          <TableCell className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(cert.id)}
                              onCheckedChange={(checked) =>
                                handleSelectOne(cert.id, !!checked)
                              }
                              aria-label={t(
                                'calibration.selectCert',
                                'Select certificate {{certNumber}}',
                                { certNumber: cert.certNumber },
                              )}
                              className="mx-auto"
                            />
                          </TableCell>
                          {visibleColumns.map((col) => {
                            const isCertNumber = col.key === 'certNumber';
                            const isLocked = col.key === 'locked';
                            return (
                              <TableCell
                                key={col.key}
                                className={cn(
                                  'py-2 px-3 text-[13px] leading-snug',
                                  col.align === 'right' &&
                                    'text-right tabular-nums',
                                  col.align === 'center' && 'text-center',
                                  col.className,
                                )}
                                onClick={
                                  isCertNumber
                                    ? (e) => handleCertNumberClick(cert, e)
                                    : isLocked
                                      ? (e) => {
                                          e.stopPropagation();
                                          if (!cert.mongoCalibrationId) return;
                                          toggleLockMutation.mutate({
                                            mongoIds: [cert.mongoCalibrationId],
                                            lock: !cert.locked,
                                          });
                                        }
                                      : undefined
                                }
                                onMouseDown={
                                  isCertNumber
                                    ? (e) => {
                                        // Prevent text selection on shift-click
                                        if (e.shiftKey) e.preventDefault();
                                      }
                                    : undefined
                                }
                              >
                                {isCertNumber ? (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className="cursor-pointer select-none hover:underline"
                                    title={t(
                                      'calibration.clickToSelect',
                                      'Click to select. Shift+Click for range. Ctrl/Cmd+Click to toggle.',
                                    )}
                                  >
                                    {col.render
                                      ? col.render(cert)
                                      : String(col.getValue(cert) ?? '') || '\u2014'}
                                  </span>
                                ) : isLocked ? (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={
                                      cert.locked
                                        ? t('calibration.unlockAria', 'Unlock certificate')
                                        : t('calibration.lockAria', 'Lock certificate')
                                    }
                                    title={
                                      cert.locked
                                        ? t('calibration.unlockTooltip', 'Unlock \u2014 requires calibrationrecords.modify permission')
                                        : t('calibration.lockTooltip', 'Lock \u2014 requires calibrationrecords.modify permission')
                                    }
                                    className="inline-flex cursor-pointer rounded p-1 hover:bg-muted"
                                  >
                                    {col.render
                                      ? col.render(cert)
                                      : String(col.getValue(cert) ?? '') || '\u2014'}
                                  </span>
                                ) : col.render ? (
                                  col.render(cert)
                                ) : (
                                  String(col.getValue(cert) ?? '') || '\u2014'
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!certificatesQuery.isLoading && totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('calibration.showing', 'Showing')}{' '}
                {(rawFilters.pageNumber || 0) *
                  (rawFilters.pageLength || 50) +
                  1}{' '}
                {t('calibration.to', 'to')}{' '}
                {Math.min(
                  ((rawFilters.pageNumber || 0) + 1) *
                    (rawFilters.pageLength || 50),
                  total,
                )}{' '}
                {t('calibration.of', 'of')} {total}{' '}
                {t('calibration.certificates', 'certificates')}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilter(
                      'pageNumber',
                      Math.max(0, (rawFilters.pageNumber || 0) - 1),
                    )
                  }
                  disabled={(rawFilters.pageNumber || 0) === 0}
                  className="h-8 text-xs"
                >
                  {t('calibration.previous', 'Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilter(
                      'pageNumber',
                      Math.min(
                        totalPages - 1,
                        (rawFilters.pageNumber || 0) + 1,
                      ),
                    )
                  }
                  disabled={
                    (rawFilters.pageNumber || 0) >= totalPages - 1
                  }
                  className="h-8 text-xs"
                >
                  {t('calibration.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isPdfColumnDialogOpen}
        onOpenChange={setIsPdfColumnDialogOpen}
      >
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{t('calibration.pdfDialog.title', 'PDF export columns')}</DialogTitle>
            <DialogDescription>
              {t('calibration.pdfDialog.selectedCount', 'Selected certificates: {{count}}', {
                count: selectedCertificates.length,
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPdfColumnDraft(visiblePdfColumnKeys)}
            >
              {t('calibration.pdfDialog.selectVisible', 'Select visible')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setPdfColumnDraft(exportableColumns.map((col) => col.key))
              }
            >
              {t('calibration.pdfDialog.selectAll', 'Select all')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPdfColumnDraft(getDefaultPdfColumnKeys())}
            >
              {t('calibration.pdfDialog.resetDefault', 'Reset default')}
            </Button>
          </div>

          <ScrollArea className="max-h-[360px] pr-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {exportableColumns.map((col) => {
                const checkboxId = `pdf-column-${col.key}`;
                return (
                  <div
                    key={col.key}
                    className="flex items-center gap-2 rounded-md border p-2"
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={pdfColumnDraft.includes(col.key)}
                      onCheckedChange={(checked) =>
                        setPdfDraftColumn(col.key, checked)
                      }
                    />
                    <Label
                      htmlFor={checkboxId}
                      className="min-w-0 flex-1 cursor-pointer leading-snug"
                    >
                      {col.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <DialogFooter className="items-center gap-3 sm:justify-between sm:space-x-0">
            <div className="text-sm text-muted-foreground">
              {pdfColumnDraft.length === 1
                ? t('calibration.pdfDialog.oneColumnSelected', '1 column selected')
                : t('calibration.pdfDialog.manyColumnsSelected', '{{count}} columns selected', {
                    count: pdfColumnDraft.length,
                  })}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPdfColumnDialogOpen(false)}
              >
                {t('common:cancel', 'Cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleExportCustomPDF}
                disabled={pdfColumnDraft.length === 0}
              >
                {t('calibration.exportPDF', 'Export PDF')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate Detail Dialog */}
      <CalibrationCertificateDialog
        mongoId={selectedMongoId}
        open={!!selectedMongoId}
        onOpenChange={(open) => !open && setSelectedMongoId(null)}
      />

      {/* Sticky bulk-action bar */}
      {selectedCertificates.length > 0 && (
        <div
          role="region"
          aria-label={t('calibration.selectionBar', 'Selection actions')}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-lg border bg-background shadow-lg px-3 py-2"
        >
          <span className="text-sm font-medium">
            {t('calibration.selectedCount', '{{count}} selected', {
              count: selectedCertificates.length,
            })}
          </span>
          <span className="text-muted-foreground">·</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={clearSelection}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {t('calibration.clearSelection', 'Clear')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={handlePrint}
          >
            <Printer className="h-3.5 w-3.5 mr-1" />
            {t('calibration.printButton', 'Print')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={handleExportVisiblePDF}
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            {t('calibration.pdfVisible', 'PDF (visible)')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={openPdfColumnDialog}
          >
            <Settings2 className="h-3.5 w-3.5 mr-1" />
            {t('calibration.pdfCustomize', 'PDF…')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={toggleLockMutation.isPending}
            onClick={() => {
              const ids = selectedCertificates
                .filter((c) => !c.locked && c.mongoCalibrationId)
                .map((c) => c.mongoCalibrationId!);
              if (ids.length === 0) {
                toast.info(t('calibration.nothingToLock', 'Nothing to lock — selection is already locked.'));
                return;
              }
              toggleLockMutation.mutate({ mongoIds: ids, lock: true });
            }}
          >
            <Lock className="h-3.5 w-3.5 mr-1" />
            {t('calibration.lock', 'Lock')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={toggleLockMutation.isPending}
            onClick={() => {
              const ids = selectedCertificates
                .filter((c) => c.locked && c.mongoCalibrationId)
                .map((c) => c.mongoCalibrationId!);
              if (ids.length === 0) {
                toast.info(t('calibration.nothingToUnlock', 'Nothing to unlock — selection is already unlocked.'));
                return;
              }
              toggleLockMutation.mutate({ mongoIds: ids, lock: false });
            }}
          >
            <LockOpen className="h-3.5 w-3.5 mr-1" />
            {t('calibration.unlock', 'Unlock')}
          </Button>
        </div>
      )}
    </div>
  );
}
